import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { BETA_CONFIG } from "@/config/beta";
import { useTeamSubscription } from "@/hooks/useTeamSubscription";

interface Subscription {
  id: string;
  status: string;
  plan_type: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
}

export function useSubscription() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { teamSubscription, isTeamPro, isTeamAdmin, loading: teamLoading } = useTeamSubscription();

  useEffect(() => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    async function fetchSubscription() {
      try {
        const { data, error } = await supabase
          .from("subscriptions")
          .select("*")
          .eq("user_id", user.id)
          .in("status", ["active", "trialing", "past_due"])
          .maybeSingle();

        if (error) {
          throw error;
        }

        setSubscription(data);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Unknown error";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    }

    fetchSubscription();

    // Subscribe to realtime changes
    const channel = supabase
      .channel("subscription-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "subscriptions",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          if (payload.eventType === "DELETE") {
            setSubscription(null);
          } else {
            setSubscription(payload.new as Subscription);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const individualIsPro = subscription?.status === "active" || subscription?.status === "trialing";
  const isPro = individualIsPro || isTeamPro;
  const isPastDue = subscription?.status === "past_due" || teamSubscription?.subscription_status === "past_due";

  const createCheckout = async (priceId: string) => {
    const { data, error } = await supabase.functions.invoke("create-checkout", {
      body: { priceId },
    });

    if (error) throw error;
    if (data?.url) {
      window.location.href = data.url;
    }
  };

  const createTeamCheckout = async (teamId: string, priceId: string, seatCount?: number) => {
    const { data, error } = await supabase.functions.invoke("create-team-checkout", {
      body: { teamId, priceId, seatCount },
    });

    if (error) throw error;
    if (data?.url) {
      window.location.href = data.url;
    }
  };

  const openPortal = async () => {
    const { data, error } = await supabase.functions.invoke("create-portal-session", {
      body: { returnUrl: window.location.href },
    });

    if (error) throw error;
    if (data?.url) {
      window.location.href = data.url;
    }
  };

  const openTeamPortal = async (teamId: string) => {
    const { data, error } = await supabase.functions.invoke("create-portal-session", {
      body: { returnUrl: window.location.href, teamId },
    });

    if (error) throw error;
    if (data?.url) {
      window.location.href = data.url;
    }
  };

  // During beta, grant full access to everyone
  if (BETA_CONFIG.isBetaMode && BETA_CONFIG.allFeaturesUnlocked) {
    return {
      subscription,
      loading,
      error,
      isPro: true,
      isPastDue: false,
      isBetaAccess: true,
      isTeamPro,
      isTeamAdmin,
      teamSubscription,
      teamLoading,
      createCheckout,
      createTeamCheckout,
      openPortal,
      openTeamPortal,
    };
  }

  return {
    subscription,
    loading: loading || teamLoading,
    error,
    isPro,
    isPastDue,
    isBetaAccess: false,
    isTeamPro,
    isTeamAdmin,
    teamSubscription,
    teamLoading,
    createCheckout,
    createTeamCheckout,
    openPortal,
    openTeamPortal,
  };
}
